/**
 * Resolve the lesson payment step's single button. Mirrors
 * `pages/booking/steps/checkoutCta.ts` — a pure function of every state the
 * button can be in, so the CTA and the submit handler read the SAME
 * expression rather than two copies that drift.
 *
 * The button names the amount whenever one is owed. A lesson the member's
 * package does not fully cover charges their card, and "Confirm Lesson" over a
 * card form does not say how much. The figure is the server quote's total;
 * nothing here computes it.
 *
 * @module pages/lessons/lessonCheckoutCta
 */

export interface LessonCtaInputs {
  /** Member's saved-methods query has not answered yet. */
  isLoadingPaymentMethods: boolean;
  /**
   * The server quote answered and priced this slot. False while it is loading,
   * when it failed, and when it refused — none of which may be booked.
   */
  priced: boolean;
  /** The quote's total, when something is owed. Null for a covered lesson. @unit dollars */
  totalDue: number | null;
  /** The quote says something is owed, so a card is collected. */
  needsCard: boolean;
  /** A saved (chargeable) method is selected. */
  hasSelectedMethod: boolean;
  /** The new-card form is rendered instead of the saved-methods list. */
  showingNewPaymentForm: boolean;
  /** A submit of the card form would actually go through. False when the form isn't mounted. */
  cardReady: boolean;
  /** `useWaiverSubmission().satisfied` — vacuously true when nothing is owed. */
  waiverSatisfied: boolean;
  /** `isOverLessonDurationLimit(durationMinutes)` — refused before submit. */
  overLimit: boolean;
  /** instructorId, bayId, date, startTime, endTime are all present. */
  selectionComplete: boolean;
  /** A card save, waiver submission, or booking creation is in flight. */
  busy: boolean;
}

export type LessonCtaAction = 'submit-card' | 'confirm';

export interface LessonCta {
  testId: 'lesson-confirm-btn';
  /** `null` when there is nothing the button could act on — it renders disabled. */
  action: LessonCtaAction | null;
  disabled: boolean;
  label: string;
}

export function resolveLessonCta(inputs: LessonCtaInputs): LessonCta {
  const {
    busy,
    priced,
    totalDue,
    needsCard,
    isLoadingPaymentMethods,
    hasSelectedMethod,
    showingNewPaymentForm,
    cardReady,
    waiverSatisfied,
    overLimit,
    selectionComplete,
  } = inputs;

  // A fully covered lesson has nothing to submit — it books directly. A
  // selection outranks a visible form, same precedence as checkoutCta.ts:
  // once a just-saved card is selected, the button must book with it even if
  // the spent form is still mounted.
  const action: LessonCtaAction | null = !needsCard
    ? 'confirm'
    : isLoadingPaymentMethods
      ? null
      : hasSelectedMethod
        ? 'confirm'
        : showingNewPaymentForm
          ? 'submit-card'
          : null; // saved methods exist but none is chargeable (bank-only rows)

  // Derived from `action`, not the raw inputs directly (same as checkoutCta.ts) —
  // otherwise a selected method still reads as actionable while the saved-methods
  // query is loading, even though `action` is null and there's nothing to submit.
  const actionable = action === 'confirm' || (action === 'submit-card' && cardReady);

  return {
    testId: 'lesson-confirm-btn',
    action,
    disabled:
      busy || !priced || overLimit || !selectionComplete || !waiverSatisfied || (needsCard && !actionable),
    label: busy ? 'Booking Lesson…' : totalDue !== null ? `Pay $${totalDue.toFixed(2)}` : 'Confirm Lesson',
  };
}
